import { useState, useCallback } from 'react';
import api from '../api/client';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

export type PushStatus = 'idle' | 'loading' | 'subscribed' | 'denied' | 'unsupported' | 'error';

export function usePushNotifications() {
  const [status, setStatus] = useState<PushStatus>(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      return 'unsupported';
    }
    if (Notification.permission === 'denied') return 'denied';
    if (Notification.permission === 'granted') return 'idle';
    return 'idle';
  });

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return false;
    }

    setStatus('loading');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        return false;
      }

      // Register service worker if needed
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) {
        reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      }
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const { data } = await api.get('/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

      // Subscribe
      let pushSub = await reg.pushManager.getSubscription();
      if (!pushSub) {
        pushSub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      }

      const json = pushSub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: pushSub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      });

      setStatus('subscribed');
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      setStatus('error');
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) return;
      const pushSub = await reg.pushManager.getSubscription();
      if (!pushSub) return;
      const json = pushSub.toJSON();
      await api.delete('/push/subscribe', {
        data: { endpoint: pushSub.endpoint, p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      });
      await pushSub.unsubscribe();
      setStatus('idle');
    } catch (err) {
      console.error('Push unsubscribe error:', err);
    }
  }, []);

  const testPush = useCallback(async () => {
    try {
      await api.post('/push/test');
      return true;
    } catch {
      return false;
    }
  }, []);

  const isSubscribed = status === 'subscribed';
  const isSupported = status !== 'unsupported';

  return { status, subscribe, unsubscribe, testPush, isSubscribed, isSupported };
}
